import { useCallback, useMemo, useState } from "react";

import {
  isPersistedSessionExpired,
  readPersistedSessionMeta,
  signOut,
} from "../api";
import { SessionContext } from "./session.constants";
import { ISessionProviderProps } from "./session.models";
import { ISession } from "@prostocheck/shared";

/**
 * Read once, at mount, so the very first render already knows to wait. Only
 * the non-sensitive metadata — this does not decrypt anything, so it stays
 * synchronous.
 */
const hasRestorableSession = (): boolean => {
  const meta = readPersistedSessionMeta();

  return Boolean(meta) && !isPersistedSessionExpired(meta!);
};

export const SessionProvider = ({
  children,
  initialSession = null,
}: ISessionProviderProps) => {
  const [session, setSession] = useState<ISession | null>(initialSession);
  const [isRestoringSession, setIsRestoringSession] = useState(
    () => !initialSession && hasRestorableSession(),
  );

  const endSession = useCallback(() => {
    setSession(current => {
      signOut(current);

      return null;
    });
  }, []);

  const value = useMemo(
    () => ({
      session,
      startSession: setSession,
      endSession,
      isRestoringSession,
      setIsRestoringSession,
    }),
    [session, endSession, isRestoringSession],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
